import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import type { RadiusCircle } from '@openx/types/targeting/geographic.ts';
import { getCircleDisplayName } from '@openx/utils/state/geoLocationSearch/utils.ts';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkAdd } from '../../shared/BulkAdd/useBulkAdd.ts';
import { BulkAddCircles } from './BulkAddCircles.tsx';
import { useCoordinates } from './hooks/useCoordinates.tsx';
import { useRadius } from './hooks/useRadius.ts';
import { RadiusFindAutoComplete } from './RadiusFindAutoComplete.tsx';
import { LOCATION_LIMIT } from './utils/constants.ts';
import { splitCoordinates } from './utils/splitCoordinates.ts';

interface Props {
  children?: ReactNode;
  circles_v2: RadiusCircle[];
  onSelect: (item: RadiusCircle) => void;
  onBulkApply: (items: RadiusCircle[]) => void;
}

export function LocationRadius({ children, onSelect, circles_v2, onBulkApply }: Props) {
  const { t } = useTranslation();
  const [isSearching, setSearching] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const {
    coordinates,
    setCoordinates,
    touchedCoordinates,
    setTouchedCoordinates,
    coordinatesError,
    setCoordinatesError,
    onCoordinatesChange,
    onCoordinatesBlur,
    onCoordinatesSelect,
    clearErrors: clearCoordsErrors,
  } = useCoordinates();
  const {
    radius,
    setRadius,
    onRadiusChange,
    radiusError,
    setRadiusError,
    clearErrors: clearRadiusErrors,
  } = useRadius();

  const isLocationLimitReached = circles_v2.length >= LOCATION_LIMIT;

  const clearErrors = useCallback(() => {
    clearCoordsErrors();
    clearRadiusErrors();
  }, [clearCoordsErrors, clearRadiusErrors]);

  const clearCoordsInput = useCallback(() => {
    setCoordinates('');
    setRadius('');
    setTouchedCoordinates(false);
    clearErrors();
  }, [setCoordinates, setRadius, setTouchedCoordinates, clearErrors]);

  const onBulkApplyCoordinates = useCallback(
    (coords: RadiusCircle[]) => {
      onBulkApply(coords);
      clearCoordsInput();
    },
    [onBulkApply, clearCoordsInput]
  );

  const { isBulkOpen, onBulkAdd, onBulkClose, onBulkOpen } = useBulkAdd<RadiusCircle[]>({
    onAdd: onBulkApplyCoordinates,
  });

  const onAdd = useCallback(() => {
    const [lat, lon] = splitCoordinates(coordinates);
    const coord = { lat: lat.toString(), lon: lon.toString(), rad: radius };
    const coordName = getCircleDisplayName(coord);

    if (circles_v2.some(cir => getCircleDisplayName(cir) === coordName)) {
      setDuplicateError(true);
      return;
    }

    onSelect(coord);
    clearCoordsInput();
  }, [circles_v2, coordinates, onSelect, radius, clearCoordsInput]);

  const toggleSearching = useCallback(() => {
    setSearching(!isSearching);
  }, [isSearching]);

  useEffect(() => {
    if (!duplicateError) {
      setCoordinatesError(null);
      setRadiusError(null);
    } else {
      setCoordinatesError(t('Duplicate coordinates'));
      setRadiusError('');
    }
  }, [duplicateError, t, setCoordinatesError, setRadiusError]);

  const isAddDisabled =
    !touchedCoordinates ||
    radiusError !== null ||
    coordinatesError !== null ||
    !radius ||
    !coordinates ||
    isLocationLimitReached;

  return (
    <>
      <Grid container spacing={1} sx={{ mr: 2 }}>
        <Grid size={3}>{children}</Grid>
        <Grid size={6} data-test="location-radius-coordinates">
          <TextField
            fullWidth
            label={t('Latitude and Longitude')}
            placeholder={t('Enter coordinates (example: 34.147785, -118.144516)')}
            onChange={event => {
              setDuplicateError(false);
              onCoordinatesChange(event);
            }}
            onBlur={() => {
              setDuplicateError(false);
              onCoordinatesBlur();
            }}
            value={coordinates}
            helperText={isLocationLimitReached ? t('The limit has been reached (10000)') : coordinatesError}
            error={coordinatesError !== null || isLocationLimitReached}
            data-test="duplicate-error"
          />
        </Grid>

        <Grid size={2} data-test="radius-coords">
          <TextField
            fullWidth
            label={t('Radius')}
            placeholder={t('Radius')}
            slotProps={{
              input: {
                disableUnderline: true,
                endAdornment: <InputAdornment position="end">MI</InputAdornment>,
              },
            }}
            onChange={event => {
              setDuplicateError(false);
              onRadiusChange(event);
            }}
            value={radius}
            helperText={radiusError}
            error={radiusError !== null || isLocationLimitReached}
          />
        </Grid>

        <Grid size={1} sx={{ mt: 2 }}>
          <Button onClick={onAdd} disabled={isAddDisabled}>
            {t('Add')}
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-end',
          mt: 1,
          mr: 2,
        }}
      >
        <Grid size={!isSearching ? 6 : 3} container>
          <Grid>
            <Button onClick={onBulkOpen}>{t('Bulk Add')}</Button>
          </Grid>
          <Grid>{!isSearching && <Button onClick={toggleSearching}>{t('Find Coordinates')}</Button>}</Grid>
        </Grid>

        {isSearching && (
          <>
            <Grid size={7} data-test="location-radius-search">
              <RadiusFindAutoComplete
                onSelect={event => {
                  setDuplicateError(false);
                  onCoordinatesSelect(event);
                }}
              />
            </Grid>

            <Grid size={2} sx={{ mt: 2 }}>
              <Button onClick={toggleSearching} variant="text">
                {t('Cancel')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      {isBulkOpen && <BulkAddCircles onClose={onBulkClose} onBulkAdd={onBulkAdd} currentItems={circles_v2} />}
    </>
  );
}
